import { baseQueryWithReauth } from './baseQuery';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

// AE4-526 — Tarifa por servicio del catálogo, resuelta por organización de la sesión.
export type TipoServicio = 'facturable' | 'documental';

export interface TarifaUnitaria {
    monto: number;
    moneda: string; // 'USD'
    unidad: string;
}

// Item del catálogo paramétrico de códigos de tarifa (servicios-nave/datos-parametricos/codigos-tarifa).
export interface CodigoTarifa {
    id: number;
    codigo: number;
    descripcion: string;
}

export interface Servicio {
    id: number;
    nombre: string;
    gerencia?: { id: number; nombre: string; activo: boolean };
    activo: boolean;
    atributos?: any[];
    prestadores?: any[];
    tipo: TipoServicio;
    codigoTarifario: number | null;
    tarifaUnitaria: TarifaUnitaria | null;
}

// Sección opcional de tarifa enviada en alta/edición (ausente/null = servicio documental).
export interface TarifaServicioDTO {
    codigoTarifario: number;
    monto: number;
    unidad: string;
}

// Envoltorio OkResponse paginado del listado (sin cambios respecto al contrato previo).
export interface ServiciosPaginados {
    data: { data: Servicio[]; count: number };
}

// AE4-566 — Cálculo de tasas de una solicitud (Generar Tasas en la liquidación).
export interface TasaSolicitud {
    codigo: number | null; // código tarifario / cargo
    descripcion: string;
    detalleCalculo: string | null; // ej. "15 USD × 120 M3" | "Cargo adicional" | "Sin tarifa del puerto"
    cantidad: number | null;
    importeUnitarioUSD: number | null;
    importeUSD: number;
    esCargoAdicional: boolean;
}

export interface CalculoTasas {
    lineas: TasaSolicitud[];
    totalUSD: number;
    moneda: string; // 'USD'
}

// AE4-566 — Cargo adicional del catálogo (selección por id en el POST de tasas).
export interface CargoAdicionalServicio {
    id: number;
    codigo: number;
    descripcion: string;
    importeUSD: number;
}

export interface ServicioNaveGiro {
    idSolicitud: number;
    servicio: string;
    proveedor: string;
    estado: string;
    idEstado: number;
    montoUSD: number | null;
    tieneTarifaPuerto: boolean;
}

export interface ServiciosNaveGiroResponse {
    puedeGenerarTasas: boolean;
    servicios: ServicioNaveGiro[];
}

export const shipServiceApi = createApi({
    reducerPath: 'serviciosNave',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'Services',
        'ServiceById',
        'RequestById',
        'ServiceRequests',
        'ServiciosNaveGiro',
        'TasasSolicitud'
    ],
    endpoints: (builder) => ({
        getManagements: builder.query({
            query: () => ({
                url: 'servicios-nave/datos-parametricos/gerencias'
            }),
            transformResponse: (response: { data: { gerencias: any } }) => response?.data?.gerencias
        }),
        getServices: builder.query({
            query: (filters: any) => ({
                url: 'servicios-nave/datos-parametricos/servicios',
                params: !!Object.keys(filters)?.length ? filters : null
            }),
            transformResponse: (response: { data: { servicios: any } }) => response?.data?.servicios
        }),
        getShipServiceRateCodes: builder.query<CodigoTarifa[], any>({
            query: () => ({
                url: 'servicios-nave/datos-parametricos/codigos-tarifa'
            }),
            transformResponse: (response: { data: { codigos: CodigoTarifa[] } }) =>
                response?.data?.codigos
        }),
        getProviders: builder.query({
            query: () => ({
                url: 'servicios-nave/datos-parametricos/prestadores'
            }),
            transformResponse: (response: { data: { prestadores: any } }) =>
                response?.data?.prestadores
        }),
        addService: builder.mutation({
            query: (body) => ({
                url: 'servicios-nave',
                method: 'POST',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['ServiceById', 'Services'])
        }),
        editService: builder.mutation({
            query: ({ serviceId, body }: any) => ({
                url: `servicios-nave/${serviceId}`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['ServiceById', 'Services'])
        }),
        getShipServices: builder.query<ServiciosPaginados, { filters: any }>({
            query: ({ filters }) => ({
                url: 'servicios-nave',
                params: filters
            }),
            providesTags: ['Services']
        }),
        getShipServiceById: builder.query<Servicio, { serviceId: any }>({
            query: ({ serviceId }) => ({
                url: `servicios-nave/${serviceId}`
            }),
            providesTags: ['ServiceById'],
            transformResponse: (response: { data: Servicio }) => response?.data
        }),
        getShipsForServices: builder.query({
            query: ({ filters }) => ({
                url: 'servicios-nave/buques',
                params: filters
            }),
            transformResponse: (response: { data: { buques: any } }) => response?.data?.buques
        }),
        addServiceRequest: builder.mutation({
            query: ({ body }) => ({
                url: 'servicios-nave/solicitudes',
                method: 'POST',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['ServiceRequests'])
        }),
        getServiceRequests: builder.query({
            query: ({ filters }) => ({
                url: 'servicios-nave/solicitudes',
                params: filters
            }),
            providesTags: ['ServiceRequests']
        }),
        getServiceRequestById: builder.query({
            query: (requestId) => ({
                url: `servicios-nave/solicitudes/${requestId}`
            }),
            providesTags: ['RequestById'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        logAttributes: builder.mutation({
            query: ({ requestId, body }) => ({
                url: `servicios-nave/solicitudes/${requestId}/provision`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['ServiceRequests', 'RequestById'])
        }),
        liquidateService: builder.mutation({
            query: ({ requestId, body }) => ({
                url: `servicios-nave/solicitudes/${requestId}/liquidacion`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['ServiceRequests', 'RequestById'])
        }),
        downloadAttachedFile: builder.query({
            query: ({ requestId, billingNumber }) => ({
                url: `servicios-nave/solicitudes/${requestId}/liquidacion/${billingNumber}`
            }),
            transformResponse: (response: { data: { presignedUrl: string } }) =>
                response?.data?.presignedUrl
        }),
        cancelRequest: builder.mutation({
            query: ({ requestId, body }) => ({
                url: `servicios-nave/solicitudes/${requestId}`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['ServiceRequests', 'RequestById'])
        }),
        uploadFileCompliance: builder.mutation({
            query: ({ requestId, formData }) => ({
                url: `servicios-nave/solicitudes/${requestId}/adjunto-provision`,
                method: 'POST',
                formData: true,
                body: formData,
            }),
            // invalidatesTags: (_, error) => (error ? [] : ['ServiceRequests', 'RequestById'])
        }),
        downloadAttachedFileCompliance: builder.query({
            query: ({ requestId, fileName }) => ({
                url: `servicios-nave/solicitudes/${requestId}/adjunto-provision/${fileName}`
            }),
            transformResponse: (response: { data: { presignedUrl: string } }) =>
                response?.data?.presignedUrl
        }),
        sendToLiquidate: builder.mutation({
            query: ({ requestId }) => ({
                url: `servicios-nave/solicitudes/${requestId}/enviar-liquidacion`,
                method: 'PATCH',
            }),
            invalidatesTags: (_, error) => (error ? [] : ['ServiceRequests', 'RequestById'])
        }),
        getServiciosNaveByGiro: builder.query<ServiciosNaveGiroResponse, number>({
            query: (idGiro) => ({
                url: `servicios-nave/giros/${idGiro}/servicios`
            }),
            providesTags: ['ServiciosNaveGiro'],
            transformResponse: (response: { data: ServiciosNaveGiroResponse }) => response?.data
        }),
        // AE4-566 — Cálculo vigente de tasas de la solicitud (solo lectura).
        getTasasVigentesSolicitud: builder.query<CalculoTasas, number | string>({
            query: (idSolicitud) => ({
                url: `servicios-nave/solicitudes/${idSolicitud}/tasas`
            }),
            providesTags: (_res, _err, idSolicitud) => [
                { type: 'TasasSolicitud', id: idSolicitud }
            ],
            transformResponse: (response: { data: CalculoTasas }) => response?.data
        }),
        // AE4-566 — Catálogo de cargos adicionales de la organización de la sesión.
        getCargosAdicionalesServicio: builder.query<CargoAdicionalServicio[], void>({
            query: () => ({
                url: 'servicios-nave/datos-parametricos/cargos-adicionales'
            }),
            transformResponse: (response: { data: { cargos: CargoAdicionalServicio[] } }) =>
                response?.data?.cargos
        }),
        // AE4-566 — Genera/regenera las tasas; reemplaza el cálculo vigente.
        generarTasasSolicitud: builder.mutation<
            CalculoTasas,
            { idSolicitud: number | string; cargosAdicionales: number[] }
        >({
            query: ({ idSolicitud, cargosAdicionales }) => ({
                url: `servicios-nave/solicitudes/${idSolicitud}/tasas`,
                method: 'POST',
                body: { cargosAdicionales }
            }),
            invalidatesTags: (_, error) => (error ? [] : ['TasasSolicitud']),
            transformResponse: (response: { data: CalculoTasas }) => response?.data
        })
    })
});

export const {
    useCancelRequestMutation,
    useLazyDownloadAttachedFileQuery,
    useLiquidateServiceMutation,
    useGetShipServiceRateCodesQuery,
    useLogAttributesMutation,
    useGetServiceRequestByIdQuery,
    useGetServiceRequestsQuery,
    useAddServiceRequestMutation,
    useGetShipsForServicesQuery,
    useEditServiceMutation,
    useGetShipServiceByIdQuery,
    useGetShipServicesQuery,
    useGetManagementsQuery,
    useGetServicesQuery,
    useLazyGetServiceRequestByIdQuery,
    useGetProvidersQuery,
    useAddServiceMutation,
    useUploadFileComplianceMutation,
    useLazyDownloadAttachedFileComplianceQuery,
    useSendToLiquidateMutation,
    useGetServiciosNaveByGiroQuery,
    useGetTasasVigentesSolicitudQuery,
    useGetCargosAdicionalesServicioQuery,
    useGenerarTasasSolicitudMutation
} = shipServiceApi;
